import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Mail, MessageCircle, FileText, Shield, HelpCircle } from 'lucide-react';
import SupportEmailPopup from '../components/SupportEmailPopup';

export default function Help() {
  const navigate = useNavigate();
  const [showSupport, setShowSupport] = useState(false);

  const helpTopics = [
    {
      icon: BookOpen,
      title: 'Getting Started Guide',
      description: 'Learn how to create an account, browse the roster, and send your first booking inquiry.',
      action: () => navigate('/faq')
    },
    {
      icon: FileText,
      title: 'FAQs',
      description: 'Quick answers about bookings, the artist roster, live streams and the virtual club.',
      action: () => navigate('/faq')
    },
    {
      icon: Shield,
      title: 'Terms & Privacy',
      description: 'Read our terms of service and privacy policy.',
      action: () => navigate('/terms')
    },
    {
      icon: Mail,
      title: 'Contact Us',
      description: 'Reach the RazientSounds team directly — email support@razientsounds.com or open a support ticket.',
      action: () => setShowSupport(true)
    }
  ];

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-6xl">
        <div className="text-center mb-12">
          <HelpCircle size={64} className="mx-auto mb-4 text-violet-400" />
          <h1 className="text-5xl font-bold gradient-text mb-4">How Can We Help?</h1>
          <p className="text-white/60 text-lg">Find resources, guides, and support for RazientSounds</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {helpTopics.map((topic, idx) => (
            <Card key={idx} className="glass-default border-white/10 hover-lift cursor-pointer" onClick={topic.action}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-violet-500/20 flex items-center justify-center mb-4">
                  <topic.icon size={32} className="text-violet-400" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{topic.title}</h3>
                <p className="text-white/60">{topic.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="glass-heavy border-white/10">
          <CardContent className="p-12 text-center">
            <MessageCircle size={48} className="mx-auto mb-4 text-violet-400" />
            <h2 className="text-3xl font-bold mb-4">Need More Help?</h2>
            <p className="text-white/60 text-lg mb-6 max-w-2xl mx-auto">
              Our support team is available Monday-Friday, 9 AM - 6 PM EST. We typically respond within 24 hours.
            </p>
            <Button
              onClick={() => window.location.href = 'mailto:support@razientsounds.com'}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 h-14 px-8 text-lg"
            >
              <Mail size={20} className="mr-2" />
              Email Support Team
            </Button>
          </CardContent>
        </Card>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="glass-default border-white/10">
            <CardContent className="p-6 text-center">
              <h4 className="font-bold text-lg mb-2">For Users</h4>
              <p className="text-sm text-white/60 mb-4">Book artists, manage events, coordinate details</p>
              <Button variant="outline" onClick={() => navigate('/faq')} className="border-white/20">
                User Guide
              </Button>
            </CardContent>
          </Card>

          <Card className="glass-default border-white/10">
            <CardContent className="p-6 text-center">
              <h4 className="font-bold text-lg mb-2">For Artists</h4>
              <p className="text-sm text-white/60 mb-4">Create profiles, accept bookings, get paid</p>
              <Button variant="outline" onClick={() => navigate('/faq')} className="border-white/20">
                Artist Guide
              </Button>
            </CardContent>
          </Card>

          <Card className="glass-default border-white/10">
            <CardContent className="p-6 text-center">
              <h4 className="font-bold text-lg mb-2">Platform Policies</h4>
              <p className="text-sm text-white/60 mb-4">Terms, privacy, and guidelines</p>
              <Button variant="outline" onClick={() => navigate('/privacy')} className="border-white/20">
                Read Policies
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <SupportEmailPopup open={showSupport} onClose={() => setShowSupport(false)} />
    </div>
  );
}
